import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  commentPostRequest,
  commentEditRequest,
} from "../../../reducers/feeds/feedPost.reducer";
import { ErrorTextHelper } from "../../../components/common";
import EmojiPicker from "emoji-picker-react";
import Emoji from "../../Messages/components/emoji";
import _ from "lodash";
import { IconButton, TextareaAutosize } from "@material-ui/core";
import { Send as SendIcon, Image as ImageIcon, Close as CloseIcon } from "@material-ui/icons";
import { rootReducersState } from "../../../reducers";
import Avatar from "../../../components/common/AvtarImage";
import Button from "../../../components/common/Button";
import FlashMessage from "../../../components/common/FlashMessage";
import { getUserDisplayJobTitle } from "../../../utils/helper";

type Inputs = {
  comment_text: string;
  comment_media?: FileList;
};

type CommentPostReason = "main-comment" | "reply-comment" | "edit-comment";

interface CommentPostProps {
  data: any;
  item?: any;
  setComment?: () => void;
  reason: CommentPostReason;
}

const CommentPost = ({ data, item, setComment, reason }: CommentPostProps) => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  const dispatch = useDispatch();
  const [togglePicker, setTogglePicker] = useState<boolean>(false);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const emojiRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentTextRef = useRef<HTMLTextAreaElement | null>(null);
  const originalTextRef = useRef("");

  const isMainComment = reason === "main-comment";
  const isReplyComment = reason === "reply-comment";
  const isEditComment = reason === "edit-comment";
  const showMediaTools = !isMainComment;

  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const currentUser = _.get(sessionReducer, "currentUser", {});
  const currentUserImage = _.get(currentUser, "profile_image", "");
  const commentText = watch("comment_text", "");

  const hideEmojis = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (emojiRef.current?.contains(target)) return;
    if (target.closest?.(".EmojiPickerReact")) return;
    setTogglePicker(false);
  };

  const syncMultiline = (element?: HTMLTextAreaElement | null) => {
    const textarea = element || commentTextRef.current;
    if (!textarea) return;
    setIsMultiline(textarea.scrollHeight > 48);
  };

  const appendEmoji = (emoji: string) => {
    const nextValue = `${getValues("comment_text") || ""}${emoji}`;
    setValue("comment_text", nextValue, { shouldDirty: true, shouldValidate: true });
    requestAnimationFrame(() => syncMultiline(commentTextRef.current));
  };

  const handleEmojiToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTogglePicker((prev) => !prev);
  };

  useEffect(() => {
    if (!isEditComment) return;

    const isImageComment = item?.comment_type === "image";
    const initialText = isImageComment ? "" : String(item?.comment_text || "");
    originalTextRef.current = initialText;
    setValue("comment_text", initialText);

    if (isImageComment && item?.comment_text) {
      setPreviewUrl(item.comment_text);
    } else {
      setPreviewUrl(null);
    }
    setSelectedMedia(null);
    requestAnimationFrame(() => syncMultiline(commentTextRef.current));
  }, [isEditComment, item?.id, item?.comment_text, item?.comment_type, setValue]);

  useEffect(() => {
    document.addEventListener("mousedown", hideEmojis);
    return () => {
      document.removeEventListener("mousedown", hideEmojis);
    };
  }, []);

  const handleMediaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      FlashMessage("Only PNG, JPEG, and GIF files are allowed", "error");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      FlashMessage("File size must be less than 5 MB.", "error");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedMedia(file);
    setPreviewUrl(URL.createObjectURL(file));
    requestAnimationFrame(() => syncMultiline(commentTextRef.current));
  };

  const removeMedia = () => {
    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedMedia(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    requestAnimationFrame(() => syncMultiline(commentTextRef.current));
  };

  const onSubmit = (formData: Inputs) => {
    const tempId = Date.now();
    const submitFormData = new FormData();
    submitFormData.append("post_id", data?.id ? data.id : item.post_id);
    submitFormData.append("temp_id", tempId.toString());
    submitFormData.append(
      "parent_id",
      isReplyComment ? _.get(item, "id", "") : ""
    );

    const postId = data?.id || item?.post_id;
    const trimmedText = formData.comment_text.trim();

    if (trimmedText) {
      submitFormData.append("comment_text", trimmedText);
    } else if (!selectedMedia) {
      submitFormData.append("comment_text", "");
    }

    if (selectedMedia) {
      submitFormData.append("comment_media", selectedMedia);
    }

    if (isEditComment) {
      const editId = _.get(item, "id", "");
      submitFormData.append("id", editId);
      submitFormData.append("comment_type", _.get(item, "comment_type", ""));
      dispatch(commentEditRequest({ id: editId, formData: submitFormData }));
      setComment?.();
    } else {
      const optimisticJobTitle = getUserDisplayJobTitle(currentUser);

      const optimisticComment = {
        id: tempId,
        post_id: data?.id || item?.post_id,
        user_id: _.get(currentUser, "id", ""),
        comment_text: formData?.comment_text?.trim() || "",
        comment_type: selectedMedia ? "image" : "text",
        user: {
          first_name: _.get(currentUser, "first_name", ""),
          last_name: _.get(currentUser, "last_name", ""),
          profile_image: _.get(currentUser, "profile_image", ""),
          id: _.get(currentUser, "id", ""),
          profile: _.get(currentUser, "profile", {}),
          job_details: optimisticJobTitle
            ? {
                title: optimisticJobTitle,
                job_title_custom: _.get(currentUser, "profile.job_title_custom", ""),
              }
            : null,
        },
        created_at: new Date().toISOString(),
        isLoading: true,
        parent_id: isReplyComment ? _.get(item, "id", null) : null,
      };

      dispatch(
        commentPostRequest({
          formData: submitFormData,
          optimisticComment,
          postId,
        })
      );
    }

    reset();
    removeMedia();
    setIsMultiline(false);
    setTogglePicker(false);
    if (commentTextRef.current) {
      commentTextRef.current.value = "";
    }
  };

  const checkKeyDown = (e: React.KeyboardEvent) => {
    if (isEditComment) return;

    const keyCode = e.keyCode ? e.keyCode : e.which;
    if (keyCode === 13 && !e.shiftKey) {
      if (selectedMedia && (isReplyComment || isMainComment)) {
        return;
      }
      handleSubmit(onSubmit)();
      e.preventDefault();
    }
  };

  const canSubmit = Boolean(selectedMedia) || Boolean(String(commentText || "").trim());

  const canUpdate =
    isEditComment &&
    (Boolean(selectedMedia) ||
      String(commentText || "").trim() !== originalTextRef.current.trim());

  const { ref: registerRef, ...commentTextRegister } = register("comment_text", {
    maxLength: {
      value: 1000,
      message: "Cannot exceed more than 1000 characters",
    },
    required: {
      value: !selectedMedia,
      message: "Comment can't be empty",
    },
    validate: (value) =>
      !!selectedMedia || value.trim() !== "" || "Comment can't be empty",
  });

  const renderTools = (className = "comment-composer__tools") => (
    <div className={className} ref={emojiRef}>
      <button
        type="button"
        className="emoji-toggle"
        aria-label="Add emoji"
        aria-expanded={togglePicker}
        onMouseDown={handleEmojiToggle}
      >
        <Emoji onClick={() => undefined} />
      </button>
      {togglePicker && (
        <div
          className="comment-composer__emoji-picker"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <EmojiPicker onEmojiClick={(e) => appendEmoji(e.emoji)} />
        </div>
      )}
      {showMediaTools && (
        <>
          <input
            type="file"
            accept="image/png, image/jpeg, image/gif"
            onChange={handleMediaChange}
            ref={fileInputRef}
            hidden
          />
          <IconButton
            aria-label="Attach image"
            onClick={() => fileInputRef.current?.click()}
            className="comment-composer__media-btn"
          >
            <ImageIcon className="material-icons" />
          </IconButton>
        </>
      )}
    </div>
  );

  const renderMediaPreview = () =>
    previewUrl ? (
      <div className="comment-media-preview">
        <img src={previewUrl} alt="Comment attachment preview" />
        <button
          type="button"
          className="media-close"
          onClick={removeMedia}
          aria-label="Remove attachment"
        >
          <CloseIcon className="close-icon" />
        </button>
        <span className="comment-media-preview__label">Image attached</span>
      </div>
    ) : null;

  const renderAttachment = () =>
    previewUrl ? (
      <div className="comment-composer__attachment">{renderMediaPreview()}</div>
    ) : null;

  const getTextareaPlaceholder = () => {
    if (previewUrl) {
      return "Add optional text...";
    }

    return isMainComment
      ? "Write your comment..."
      : isReplyComment
        ? "Write a reply..."
        : "Edit your comment...";
  };

  const renderTextarea = (className = "") => (
    <TextareaAutosize
      className={className}
      placeholder={getTextareaPlaceholder()}
      onKeyDown={checkKeyDown}
      {...commentTextRegister}
      onChange={(event) => {
        commentTextRegister.onChange(event);
        syncMultiline(event.currentTarget);
      }}
      ref={(element) => {
        registerRef(element);
        commentTextRef.current = element;
        syncMultiline(element);
      }}
    />
  );

  const getFieldClassName = (fieldModifier = "") =>
    [
      "comment-composer__field",
      fieldModifier,
      isMultiline ? "is-multiline" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const renderComposerField = (fieldModifier: string) => (
    <div className={getFieldClassName(fieldModifier)}>
      {renderTextarea()}
      {renderTools()}
    </div>
  );

  const renderSubmitActions = () => {
    if (isReplyComment) return null;

    if (!selectedMedia) return null;

    return (
      <div className="comment-composer__submit">
        <Button type="submit" color="primary" disabled={!canSubmit}>
          {isEditComment ? "Update" : isMainComment ? "Comment" : "Post"}
        </Button>
      </div>
    );
  };

  if (isEditComment) {
    return (
      <form
        className={`comment-composer comment-composer--${reason}${previewUrl ? " has-attachment" : ""}`}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="comment-composer__panel">
          {renderAttachment()}
          {renderComposerField("comment-composer__field--edit")}

          {_.get(errors, "comment_text.message") && !canSubmit && (
            <ErrorTextHelper text={_.get(errors, "comment_text.message", "")} />
          )}

          <div className="comment-composer__actions">
            <button
              type="button"
              className="comment-composer__cancel-btn"
              onClick={() => setComment?.()}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="comment-composer__update-btn"
              disabled={!canUpdate}
            >
              Update
            </button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <form
      className={`comment-composer comment-composer--${reason}${previewUrl ? " has-attachment" : ""}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="comment-composer__row">
        {!isEditComment && (
          <div className="comment-composer__avatar">
            <Avatar src={currentUserImage} />
          </div>
        )}

        <div className="comment-composer__main">
          {renderAttachment()}

          {isReplyComment ? (
            <>
              <div className="comment-composer__reply-wrapper">
                {renderComposerField("comment-composer__field--reply")}
                <button
                  type="submit"
                  className="comment-composer__send"
                  aria-label="Send reply"
                  disabled={!canSubmit}
                >
                  <SendIcon />
                </button>
              </div>
              {_.get(errors, "comment_text.message") && !canSubmit && (
                <ErrorTextHelper text={_.get(errors, "comment_text.message", "")} />
              )}
            </>
          ) : (
            <>
              <div className={getFieldClassName()}>
                {renderTextarea()}
                {renderTools()}
              </div>

              {_.get(errors, "comment_text.message") && !canSubmit && (
                <ErrorTextHelper text={_.get(errors, "comment_text.message", "")} />
              )}

              {renderSubmitActions()}
            </>
          )}
        </div>
      </div>
    </form>
  );
};

export default CommentPost;
